import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ConnectionStatus } from '@/views/connections/connections-types';
import { JobTipService, adminAddJobTip, adminDeleteJobTip } from '@/functions/services-functions';
import { confirmDialogue, generalError } from '@/functions/system-modal-functions';
import { i18n } from '@/utilities/vue-i18n';
import { ModalIconType } from '../system-modals/system-modals';

interface SettingsType {
  connection: JobTipService;
  loading: boolean;
  company: number;
}

export default defineComponent({
  props: {
    connection: { type: Object as PropType<JobTipService>, required: true },
    companyId: { type: Number, required: false },
    allowDelete: { type: Boolean, required: false, default: false },
    isAdmin: { type: Boolean, required: false, default: false },
    loading: { type: Boolean, required: false, default: false },
  },
  setup(props: any, context: any) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company,
    });

    watch(() => props.connection, (v: JobTipService) => settings.connection = { ...v }, { deep: true });
    watch(() => props.loading, (v: boolean) => settings.loading = v ?? settings.loading);
    watch(() => props.companyId, (v: number) => settings.company = v ?? settings.company);

    const enable = async () => {
      if (settings.loading || !props.isAdmin) return;
      settings.loading = true;

      const confirmed = await confirmDialogue(
        'JobTip',
        i18n.t('components.connection-jobtip.confirm-enable') as string,
        ModalIconType.question
      );

      if (confirmed) {
        const service = await adminAddJobTip(settings.company);
        if (service) {
          settings.connection = service;
          context.emit('updated', settings.connection);
        } else {
          await generalError(false);
        }
      }
      settings.loading = false;
    };

    const disable = async () => {
      if (!props.allowDelete || !props.isAdmin || settings.loading) return;
      settings.loading = true;

      const confirmed = await confirmDialogue(
        'JobTip',
        i18n.t('components.connection-jobtip.disable-jobtip-service') as string,
        ModalIconType.warning
      );

      if (confirmed) {
        if (await adminDeleteJobTip(settings.connection.id)) {
          context.emit('updated', null);
        } else {
          await generalError(false);
        }
      }
      settings.loading = false;
    };

    return { settings, enable, disable, ConnectionStatus };
  }
});